import { Link, useLocation } from "react-router-dom";
import { useContext, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Search, User, Crown, Bell, Home, Tv, Globe, Heart, Menu, X } from "lucide-react";
import axios from "axios";
import { SettingsContext } from "@/App";
import { cn } from "@/lib/utils";
import LanguageSelector from "./LanguageSelector";
import NotificationBell from "./NotificationBell";
import { useSiteNavigation, pageLabel } from "@/hooks/useSiteNavigation";

function getUser() {
  try { return JSON.parse(localStorage.getItem('user_data')); } catch { return null; }
}

export default function Navbar() {
  const { settings } = useContext(SettingsContext);
  const { t, i18n } = useTranslation();
  const headerPages = useSiteNavigation("header");
  const [user, setUser] = useState(getUser());
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const location = useLocation();

  useEffect(() => {
    const check = () => setUser(getUser());
    window.addEventListener('storage', check);
    const interval = setInterval(check, 2000);
    return () => { window.removeEventListener('storage', check); clearInterval(interval); };
  }, []);

  if (location.pathname.startsWith("/admin") || location.pathname.startsWith("/moderator")) return null;

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/" && !location.search;
    return location.pathname === path;
  };

  const mobileNav = [
    { icon: Home, label: t("nav.home"), path: "/" },
    { icon: Tv, label: t("nav.live"), path: "/live" },
    { icon: Globe, label: t("nav.countries"), path: "/countries" },
    { icon: Heart, label: t("nav.favorites"), path: "/account?tab=favorites" },
    { icon: User, label: t("nav.account"), path: "/account" },
  ];

  return (
    <>
      <header
        className={cn(
          "sticky top-0 z-30 border-b border-white/[0.06] backdrop-blur-xl",
          "bg-[#050814]/80"
        )}
        data-testid="navbar"
      >
        <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center lg:hidden">
            <Link to="/" className="flex items-center">
              <img src="/djilu-logo.png" alt="DJILU.COM" className="h-12 w-auto object-contain" />
            </Link>
          </div>

          <div className="hidden sm:flex flex-1 max-w-xl">
            <div className="relative w-full group">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#94A3B8] group-focus-within:text-[#FFC928] transition-colors" size={18} />
              <input
                type="text"
                placeholder={t("nav.search")}
                data-testid="search-input"
                className="w-full pl-11 pr-4 py-2.5 rounded-2xl bg-white/[0.05] border border-white/[0.08] text-white placeholder:text-[#94A3B8]/60 focus:outline-none focus:ring-2 focus:ring-[#FFC928]/20 focus:border-[#FFC928]/30 backdrop-blur-sm transition-all text-sm"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && searchQuery.trim()) {
                    window.location.href = `/?search=${encodeURIComponent(searchQuery)}`;
                  }
                }}
              />
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="sm:hidden p-2 text-[#94A3B8] hover:text-white transition-colors"
              data-testid="mobile-search-toggle"
            >
              {searchOpen ? <X size={20} /> : <Search size={20} />}
            </button>

            <LanguageSelector />

            {headerPages.length > 0 && (
              <nav className="hidden lg:flex items-center gap-4 mr-1" data-testid="navbar-pages">
                {headerPages.map((p) => {
                  const label = pageLabel(p, i18n);
                  return p.new_tab ? (
                    <a key={p.slug} href={p.url} target="_blank" rel="noopener noreferrer"
                      className="text-sm font-medium text-[#94A3B8] hover:text-white transition-colors"
                      data-testid={`navbar-page-${p.slug}`}>{label}</a>
                  ) : (
                    <Link key={p.slug} to={p.url}
                      className="text-sm font-medium text-[#94A3B8] hover:text-white transition-colors"
                      data-testid={`navbar-page-${p.slug}`}>{label}</Link>
                  );
                })}
              </nav>
            )}

            {user && <NotificationBell />}

            {settings?.membership_enabled !== false && (
              <Link
                to="/membership"
                className="hidden sm:flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-[#FFC928] to-[#FFD85A] text-black rounded-xl text-sm font-semibold hover:shadow-[0_0_20px_rgba(255,201,40,0.3)] transition-all"
                data-testid="membership-link"
              >
                <Crown size={14} />
                <span>{t("nav.premium")}</span>
              </Link>
            )}

            {user ? (
              <Link
                to="/account"
                className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/[0.05] border border-white/[0.08] hover:bg-white/[0.08] transition-all"
                data-testid="account-link"
              >
                {user.avatar_url ? (
                  <img src={user.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover border border-white/20" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-[#FFC928]/20 flex items-center justify-center text-[#FFC928] text-xs font-bold">
                    {(user.name || user.email || "U")[0].toUpperCase()}
                  </div>
                )}
                <span className="hidden sm:inline text-sm font-medium text-white">{user.name || t("nav.account")}</span>
              </Link>
            ) : (
              <Link
                to="/account"
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-white/[0.05] border border-white/[0.08] text-white text-sm font-medium hover:bg-white/[0.08] transition-all"
                data-testid="login-link"
              >
                <User size={16} />
                <span className="hidden sm:inline">{t("nav.signIn")}</span>
              </Link>
            )}
          </div>
        </div>

        {searchOpen && (
          <div className="sm:hidden px-4 pb-3 border-t border-white/[0.06]">
            <div className="relative mt-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" size={16} />
              <input
                type="text"
                placeholder={t("nav.searchMobile")}
                autoFocus
                data-testid="mobile-search-input"
                className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-white/[0.05] border border-white/[0.08] text-white placeholder:text-[#94A3B8]/60 focus:outline-none focus:ring-2 focus:ring-[#FFC928]/20 text-sm"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && searchQuery.trim()) {
                    window.location.href = `/?search=${encodeURIComponent(searchQuery)}`;
                    setSearchOpen(false);
                  }
                }}
              />
            </div>
          </div>
        )}
      </header>

      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-[#0A0F1E]/95 backdrop-blur-xl border-t border-white/[0.06] safe-area-bottom" data-testid="mobile-nav">
        <div className="flex items-center justify-around h-16 px-2">
          {mobileNav.map((item) => {
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className={cn(
                  "flex flex-col items-center gap-0.5 py-1 px-3 rounded-xl transition-all",
                  active ? "text-[#FFC928]" : "text-[#94A3B8]"
                )}
              >
                <item.icon size={20} className={cn(active && "drop-shadow-[0_0_6px_rgba(255,201,40,0.5)]")} />
                <span className="text-[10px] font-medium">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </>
  );
}
